import React, { useState } from 'react';
import { RadioGroupItem } from "@/components/ui/radio-group";
import { RadioGroupWithChildren } from '@/components/ui/radio-group-with-children';
import { LabelWithChildren } from '@/components/ui/label-with-children';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { 
  Info, 
  Users, 
  Briefcase, 
  Building, 
  Zap, 
  Target, 
  Shield,
  TrendingUp,
  Brain,
  BarChart3
} from 'lucide-react';

interface AccountTypeSelectorProps {
  accountType: string;
  setAccountType: (type: string) => void;
}

interface PlanDetails {
  id: string;
  name: string;
  price: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  details: {
    capacity: string;
    aiModels: string[];
    backtesting: string;
    compliance: string;
    support: string;
  };
}

const planDetails: PlanDetails[] = [
  {
    id: 'quant',
    name: 'Quant',
    price: '$100/seat/month',
    subtitle: 'For solo traders & small RIAs',
    icon: <Users className="w-5 h-5" />,
    color: 'from-blue-500 to-blue-600',
    features: ['1 seat', '$1M AUM capacity', 'Basic AI models', 'Standard backtesting'],
    details: {
      capacity: '1 seat, $1M AUM capacity',
      aiModels: ['ARIMA', 'XGBoost'],
      backtesting: 'Standard backtesting + 3 Monte Carlo/month',
      compliance: 'Paper trading, basic compliance alerts',
      support: 'Standard support'
    }
  },
  {
    id: 'quant-pro',
    name: 'Quant Pro',
    price: '$500/seat/month',
    subtitle: 'For prop desks & growing RIAs',
    icon: <Briefcase className="w-5 h-5" />,
    color: 'from-purple-500 to-purple-600',
    features: ['3-5 seats', '$6M AUM capacity', 'Advanced AI', 'Enhanced backtesting'],
    details: {
      capacity: '3-5 seats, $6M AUM capacity',
      aiModels: ['StewieQuant', 'LSTM', 'Ensemble'],
      backtesting: 'Enhanced backtesting + 10 Monte Carlo/month',
      compliance: 'Full compliance suite, communications monitoring',
      support: 'Priority support'
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom pricing',
    subtitle: 'For established RIAs & hedge funds',
    icon: <Building className="w-5 h-5" />,
    color: 'from-amber-500 to-amber-600',
    features: ['Custom seats', '$30M+ AUM capacity', 'All AI models', 'Unlimited simulations'],
    details: {
      capacity: 'Custom seats, $30M+ AUM capacity',
      aiModels: ['Dijkstra', 'RL DQN', 'HDK', 'All models'],
      backtesting: 'Unlimited Monte Carlo simulations',
      compliance: 'Full compliance, tax efficiency',
      support: 'Dedicated support team'
    }
  }
];

const keyBenefits = [
  { icon: <Brain className="w-4 h-4" />, text: 'Stewie Chat AI assistant' },
  { icon: <TrendingUp className="w-4 h-4" />, text: 'Paper trading capabilities' },
  { icon: <Zap className="w-4 h-4" />, text: 'API access (varying levels)' },
  { icon: <BarChart3 className="w-4 h-4" />, text: 'AUM + Compute credit system' },
  { icon: <Target className="w-4 h-4" />, text: '10 basis points annual AUM pricing' }
];

export const AccountTypeSelector: React.FC<AccountTypeSelectorProps> = ({ 
  accountType, 
  setAccountType 
}) => {
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<PlanDetails | null>(null);

  const PlanDetailsModal = ({ plan }: { plan: PlanDetails }) => (
    <DialogContent className="max-w-2xl bg-gray-800 border-gray-700">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 text-white">
          <div className={`p-2 rounded-lg bg-gradient-to-r ${plan.color}`}>
            {plan.icon}
          </div>
          {plan.name}
          <Badge variant="secondary" className="bg-gray-700 text-gray-300">
            {plan.price}
          </Badge>
        </DialogTitle>
        <DialogDescription className="text-gray-400">
          {plan.subtitle}
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-gray-700/50 border-gray-600">
            <CardContent className="p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Capacity
              </h4>
              <p className="text-gray-300 text-sm">{plan.details.capacity}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-700/50 border-gray-600">
            <CardContent className="p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                AI Models
              </h4>
              <div className="flex flex-wrap gap-1">
                {plan.details.aiModels.map((model, index) => (
                  <Badge key={index} variant="outline" className="text-xs border-gray-500 text-gray-300">
                    {model}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-700/50 border-gray-600">
            <CardContent className="p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Backtesting
              </h4>
              <p className="text-gray-300 text-sm">{plan.details.backtesting}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-700/50 border-gray-600">
            <CardContent className="p-4">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Compliance
              </h4>
              <p className="text-gray-300 text-sm">{plan.details.compliance}</p>
            </CardContent>
          </Card>
        </div>
        
        <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <CardContent className="p-4">
            <h4 className="font-semibold text-white mb-3">Key Benefits Across All Plans</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {keyBenefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                  <div className="text-blue-400">{benefit.icon}</div>
                  {benefit.text}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DialogContent>
  );

  return (
    <div className="space-y-4">
      <LabelWithChildren className="text-sm font-medium text-gray-300">
        Choose Your Plan
      </LabelWithChildren>
      
      <RadioGroupWithChildren 
        value={accountType} 
        onValueChange={setAccountType}
        className="space-y-3"
      >
        {planDetails.map((plan) => (
          <div key={plan.id} className="relative">
            <label 
              htmlFor={plan.id}
              className={`block cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
                accountType === plan.id 
                  ? 'ring-2 ring-blue-500 shadow-lg shadow-blue-500/20' 
                  : 'hover:ring-1 hover:ring-gray-500'
              }`}
            >
              <Card className={`${
                accountType === plan.id 
                  ? 'bg-gradient-to-r from-gray-700/80 to-gray-600/80 border-blue-500' 
                  : 'bg-gray-700/40 border-gray-600 hover:bg-gray-700/60'
              } transition-all duration-200`}>
                <CardContent className="p-4">
                                     <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                     <div className="flex items-center space-x-3 flex-1 min-w-0">
                       <RadioGroupItem value={plan.id} id={plan.id} className="sr-only" />
                       <div className={`p-2 rounded-lg bg-gradient-to-r ${plan.color} text-white flex-shrink-0`}>
                         {plan.icon}
                       </div>
                       <div className="flex-1 min-w-0">
                         <div className="flex items-center gap-2 mb-1 flex-wrap">
                           <h3 className="font-semibold text-white">{plan.name}</h3>
                           <Badge variant="secondary" className="bg-gray-600 text-gray-200 text-xs">
                             {plan.price}
                           </Badge>
                         </div>
                         <p className="text-gray-400 text-sm mb-2">{plan.subtitle}</p>
                         <div className="flex flex-wrap gap-1">
                           {plan.features.map((feature, index) => (
                             <span 
                               key={index} 
                               className="text-xs text-gray-300 bg-gray-600/50 px-2 py-1 rounded"
                             >
                               {feature}
                             </span>
                           ))}
                         </div>
                       </div>
                     </div>
                     
                     <div className="flex items-center gap-2 flex-shrink-0 sm:ml-2">
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-blue-400 hover:bg-blue-500/10"
                            onClick={(e) => e.preventDefault()}
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 bg-gray-800 border-gray-700">
                          <div className="space-y-2">
                            <h4 className="font-semibold text-white">{plan.name} Highlights</h4>
                            <p className="text-sm text-gray-400">{plan.subtitle}</p>
                            <div className="text-xs text-gray-400">
                              Click the info button for full details
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedPlanDetails(plan);
                            }}
                          >
                            Details
                          </Button>
                        </DialogTrigger>
                        <PlanDetailsModal plan={plan} />
                      </Dialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </label>
          </div>
        ))}
      </RadioGroupWithChildren>
      
      <div className="text-xs text-gray-500 text-center pt-2">
        <p>All plans include compute credits at 1/10 of AUM credit cost</p>
      </div>
    </div>
  );
};
